import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { BIOSDK_API_BASE_URL, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import {
  EXTRACT_VARIANT_GUIDE_ROWS,
  CONTEXT_IRIS_EXTRACT,
  buildAcceptAttribute,
  formatListLabel,
  ISO_RECORD_LABEL,
  isPlainImageForContext,
  isIsoRecordInput,
  API_VARIANT_RAW,
  API_VARIANT_NIST,
  API_VARIANT_MINEX,
  MODALITY_IRIS,
  MINEX_FACE_IRIS_BLOCKED_MESSAGE,
  normalizeApiVariant,
} from './biosdkRoutes';
import { useBiosdkRoute } from './hooks/useBiosdkRoute';
import VariantBlockedBanner from './components/VariantBlockedBanner';
import RoutePreviewChip from './components/RoutePreviewChip';
import InputTypeBadge from './components/InputTypeBadge';
import VariantStatusBanner from './components/VariantStatusBanner';
import RequestSummaryCard from './components/RequestSummaryCard';
import VariantTestingGuide from './components/VariantTestingGuide';
import {
  NIST_IRIS_SCENARIO_K_FRAME,
  NIST_IRIS_SCENARIO_SINGLE,
  NIST_IRIS_SCENARIOS,
} from './nistConstants';
import { extractNistIrisTemplates } from './nistIrisMatch';
import { buildNistIrisExtractSummary } from './nistExtractSummary';
import NistScenarioSelector from './components/NistScenarioSelector';
import MultiFileUpload from './components/MultiFileUpload';
import { isBchiTemplate } from './bdbUtils';
import { Eye, Upload, Download, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function IrisExtractor({ apiVariant = 'mosip' }) {
  const isNistMode = normalizeApiVariant(apiVariant) === API_VARIANT_NIST;
  const isMinexMode = normalizeApiVariant(apiVariant) === API_VARIANT_MINEX;
  const [file, setFile] = useState(null);
  const [irisType, setIrisType] = useState('Left');
  const [templateBlobUrl, setTemplateBlobUrl] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [jp2isoImageUrl, setJp2IsoImageUrl] = useState(null);
  const [timing, setTiming] = useState({
    convertInput: 0,
    convertJpeg: 0,
    extractionDurationMilliSeconds: 0,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [requestSummary, setRequestSummary] = useState(null);
  const [nistScenario, setNistScenario] = useState(NIST_IRIS_SCENARIO_SINGLE);
  const [nistFiles, setNistFiles] = useState([]);
  const [nistExtractResults, setNistExtractResults] = useState([]);
  const [nistExtractSummary, setNistExtractSummary] = useState(null);

  const activeFiles = isNistMode ? nistFiles : file ? [file] : [];
  const fileExtension = activeFiles[0] ? activeFiles[0].name.split('.').pop().toLowerCase() : '';
  const fileExtensions = useMemo(() => (fileExtension ? [fileExtension] : []), [fileExtension]);
  const hasActiveInput = activeFiles.length > 0;
  const { route, variantStatus, makeRequestSummary } = useBiosdkRoute(
    BIOSDK_API_BASE_URL,
    'extract-template',
    fileExtensions,
    apiVariant,
    CONTEXT_IRIS_EXTRACT,
    isNistMode || isMinexMode ? MODALITY_IRIS : undefined
  );

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setExtractResult(null);
    setRequestSummary(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
    setTemplateBlobUrl(null);
    setJp2IsoImageUrl(null);
    setInJpegImageUrl(null);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let startConvertInput = 0;
    let convertInputDuration = 0;

    try {
      if (isPlainImageForContext(fileExtension, CONTEXT_IRIS_EXTRACT)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
      } else if (isIsoRecordInput(fileExtension, CONTEXT_IRIS_EXTRACT)) {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        startConvertInput = performance.now();

        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=IRIS`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
      } else if (fileExtension === 'jp2') {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=IRIS`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const isoBlob = new Blob([isoResponse.data], { type: 'application/octet-stream' });
        const isoUrl = URL.createObjectURL(isoBlob);
        setJp2IsoImageUrl(isoUrl);

        const isoFormData = new FormData();
        isoFormData.append('file', isoBlob, 'converted-jp2.iso');

        const jpegResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=IRIS`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const jpegBlob = new Blob([jpegResponse.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
      } else {
        setError('Unsupported file type. Please upload JPEG, PNG, BMP, JP2, or ISO files.');
        setFile(null);
        return;
      }

      setTiming((prevTiming) => ({
        ...prevTiming,
        convertInput: convertInputDuration,
      }));
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true);
    setError(null);
    setExtractResult(null);
    setTemplateBlobUrl(null);

    const extractFiles = isNistMode ? nistFiles : file ? [file] : [];
    if (!extractFiles.length) {
      setError('Please upload an iris image file.');
      setIsProcessing(false);
      return;
    }

    if (isNistMode && nistScenario === NIST_IRIS_SCENARIO_K_FRAME && extractFiles.length < 2) {
      setError('K-frame enroll requires at least 2 iris frames.');
      setIsProcessing(false);
      return;
    }

    if (variantStatus?.isRawBlocked || variantStatus?.isNistBlocked || variantStatus?.isMinexBlocked) {
      setError(variantStatus.blockedReason);
      setIsProcessing(false);
      return;
    }

    if (isNistMode) {
      try {
        const startExtract = performance.now();
        const { templates: parsed } = await extractNistIrisTemplates(
          BIOSDK_API_BASE_URL,
          extractFiles,
          nistScenario,
          irisType
        );
        const extractDuration = performance.now() - startExtract;
        const validTemplates = parsed.filter((seg) => seg.bdbBytes?.length > 0);
        const summary = buildNistIrisExtractSummary(nistScenario, validTemplates.length, extractFiles.length);
        setNistExtractSummary(summary);
        const outputs = validTemplates.map((seg, idx) => {
          const u8 = seg.bdbBytes;
          const isBchi = isBchiTemplate(u8);
          let templateSummary = {
            formatLabel: isBchi ? 'BCHI v1 (K-frame fused)' : 'OpenIris template (JSON in BDB)',
            parsed: false,
          };
          if (!isBchi) {
            try {
              const text = new TextDecoder().decode(u8);
              const j = JSON.parse(text);
              templateSummary = {
                formatLabel: 'OpenIris (OPENIRIS_V1)',
                parsed: true,
                modelName: j.model,
                schemaVersion: j.format,
              };
            } catch {
              /* non-JSON */
            }
          }
          return {
            index: idx,
            blobUrl: URL.createObjectURL(new Blob([u8], { type: 'application/octet-stream' })),
            bdbBytes: u8,
            templateSummary,
            isBchi,
          };
        });
        setNistExtractResults(outputs);
        if (outputs[0]) setTemplateBlobUrl(outputs[0].blobUrl);
        setExtractResult({
          status: 'success',
          message: summary.description,
          details: {
            irisType,
            outputSizeBytes: outputs[0]?.bdbBytes?.length || 0,
            templateSummary: outputs[0]?.templateSummary,
            templateCount: outputs.length,
            summaryTitle: summary.title,
            summaryDetail: summary.detail,
          },
        });
        setRequestSummary({
          variant: 'NIST variant',
          endpoint: '/raw/extract-template',
          operation: 'extract-template',
          inputs: extractFiles.map((f) => f.name).join(', '),
          extra: {
            Scenario: nistScenario,
            Summary: summary.description,
            'Input frames': String(extractFiles.length),
            'Templates extracted': String(outputs.length),
            'Output format': outputs[0]?.isBchi ? 'BCHI v1' : 'OpenIris JSON',
          },
        });
        setTiming({
          convertInput: 0,
          convertJpeg: 0,
          extractionDurationMilliSeconds: extractDuration,
        });
        setShowAdvanced(true);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'NIST iris extraction failed');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let irisbdbPlaceholder = '';
    let convertJpegDuration = 0;

    try {
      const startConvert = performance.now();
      if (fileExtension === 'jp2') {
        if (!jp2isoImageUrl) throw new Error('JP2 preview not ready; re-select the file.');
        const isoBuf = await fetch(jp2isoImageUrl).then((res) => res.arrayBuffer());
        convertJpegDuration = performance.now() - startConvert;
        irisbdbPlaceholder = toSignedByteJson(isoBuf);
      } else if (isPlainImageForContext(fileExtension, CONTEXT_IRIS_EXTRACT)) {
        const formData = new FormData();
        formData.append('file', file);
        const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=IRIS`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        convertJpegDuration = performance.now() - startConvert;
        irisbdbPlaceholder = toSignedByteJson(isoResponse.data);
      } else if (isIsoRecordInput(fileExtension, CONTEXT_IRIS_EXTRACT)) {
        const isoArrayBuffer = await file.arrayBuffer();
        convertJpegDuration = performance.now() - startConvert;
        irisbdbPlaceholder = toSignedByteJson(isoArrayBuffer);
      } else {
        throw new Error('Unsupported file type.');
      }

      const templateResponse = await fetch('/iris_extract_inner_request-template.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const updatedTemplate = templateText
        .replace(/<iris_subtype>/g, irisType)
        .replace(/irisbdb/g, irisbdbPlaceholder);

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = arrayBufferToBase64(new TextEncoder().encode(updatedTemplate));
      const finalRequestBodyExtract = versionRequestTemplateText.replace('encoded_request', base64EncodedExtract);

      const startExtract = performance.now();
      const extractUrl = route.url;
      logBiosdkUiRoute('IrisExtractor', extractUrl.includes('/raw/') ? 'rawRoute' : 'isoRoute', extractUrl, {
        operation: 'extract-template',
      });
      const extractResponse = await axios.post(extractUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });
      const extractDuration = performance.now() - startExtract;

      const segment = extractResponse.data?.response?.response?.segments?.[0];
      const bdb = segment?.bdb;
      const others = segment?.others || {};
      const extractionMs = Number(others?.extractionDurationMilliSeconds) || extractDuration;

      if (!bdb || !bdb.length) {
        const errMsg =
          extractResponse.data?.response?.statusMessage ||
          extractResponse.data?.errors?.[0]?.message ||
          'Extraction returned an empty template (check BioSDK logs and OpenIris service).';
        throw new Error(errMsg);
      }

      const u8 = new Uint8Array(bdb);
      let templateSummary = { formatLabel: 'OpenIris template (JSON in BDB)', parsed: false };
      try {
        const text = new TextDecoder().decode(u8);
        const j = JSON.parse(text);
        templateSummary = {
          formatLabel: 'OpenIris (OPENIRIS_V1)',
          parsed: true,
          modelName: j.model_name,
          schemaVersion: j.schema_version,
        };
      } catch {
        /* binary or non-JSON BDB */
      }

      const tplUrl = URL.createObjectURL(new Blob([u8], { type: 'application/octet-stream' }));
      setTemplateBlobUrl(tplUrl);

      setExtractResult({
        status: 'success',
        message: 'Iris template extracted via BioSDK (OpenIris)',
        details: {
          irisType,
          outputSizeBytes: u8.length,
          templateSummary,
        },
      });
      setRequestSummary(makeRequestSummary('extract-template', file.name));

      setTiming({
        convertInput: timing.convertInput,
        convertJpeg: convertJpegDuration,
        extractionDurationMilliSeconds: extractionMs,
      });

      setShowAdvanced(true);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (templateBlobUrl) {
      const link = document.createElement('a');
      link.href = templateBlobUrl;
      link.download = 'extracted-image.iso';
      link.click();
    }
  };

  const handleReset = () => {
    setFile(null);
    setNistFiles([]);
    setNistScenario(NIST_IRIS_SCENARIO_SINGLE);
    setNistExtractResults([]);
    setNistExtractSummary(null);
    setIrisType('Left');
    setTemplateBlobUrl(null);
    setInJpegImageUrl(null);
    setJp2IsoImageUrl(null);
    setExtractResult(null);
    setRequestSummary(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card iris">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Eye className="h-8 w-8 text-iris" />
              <CardTitle className="text-3xl font-bold text-gray-800">Iris Extractor</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              {isNistMode
                ? 'NIST / FRVT iris extract — OpenIris via /raw/extract-template'
                : 'Extract via unified /extract-template — MOSIP variant: /mosip/extract-template'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <VariantBlockedBanner
              apiVariant={apiVariant}
              message={isMinexMode ? MINEX_FACE_IRIS_BLOCKED_MESSAGE : null}
            />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="irisType" className="text-gray-700 font-medium">
                    Iris Type
                  </Label>
                  <Select
                    id="irisType"
                    value={irisType}
                    onChange={(e) => setIrisType(e.target.value)}
                    className="w-full"
                  >
                    <option value="Left">Left Eye</option>
                    <option value="Right">Right Eye</option>
                  </Select>
                </div>

                {isNistMode && (
                  <NistScenarioSelector
                    label="NIST extract scenario"
                    scenarios={NIST_IRIS_SCENARIOS}
                    value={nistScenario}
                    onChange={(v) => {
                      setNistScenario(v);
                      setNistFiles([]);
                      setFile(null);
                      setInJpegImageUrl(null);
                      setNistExtractResults([]);
                      setExtractResult(null);
                      setError(null);
                    }}
                  />
                )}

                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    {isNistMode && nistScenario === NIST_IRIS_SCENARIO_K_FRAME
                      ? 'Upload K iris frames (same eye)'
                      : 'Upload Iris Image'}
                  </Label>
                  {isNistMode ? (
                    <MultiFileUpload
                      files={nistFiles}
                      onChange={(next) => {
                        setNistFiles(next);
                        setFile(next[0] || null);
                        setInJpegImageUrl(
                          next[0] && next[0].type?.startsWith('image/')
                            ? URL.createObjectURL(next[0])
                            : null
                        );
                        setNistExtractResults([]);
                        setExtractResult(null);
                        setError(null);
                      }}
                      accept={buildAcceptAttribute(apiVariant, CONTEXT_IRIS_EXTRACT)}
                      multiple={nistScenario === NIST_IRIS_SCENARIO_K_FRAME}
                      hint={`NIST: ${formatListLabel(CONTEXT_IRIS_EXTRACT)} → /raw/extract-template (raw bytes)`}
                    />
                  ) : (
                    <div className="upload-zone">
                      <input
                        type="file"
                        id="file"
                        accept={buildAcceptAttribute(apiVariant, CONTEXT_IRIS_EXTRACT)}
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="file" className="cursor-pointer">
                        <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                        {normalizeApiVariant(apiVariant) === API_VARIANT_RAW ? (
                          <p className="text-xs text-gray-400 mt-1">
                            Supported: {formatListLabel(CONTEXT_IRIS_EXTRACT)} → /raw/…
                          </p>
                        ) : (
                          <>
                            <p className="text-xs text-gray-400 mt-1">
                              Plain image: {formatListLabel(CONTEXT_IRIS_EXTRACT)} → /raw/…
                            </p>
                            <p className="text-xs text-gray-400">{ISO_RECORD_LABEL}</p>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {hasActiveInput && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded iris" className="preview-image" />
                      <Badge variant="iris" className="absolute top-2 right-2">
                        {irisType} Eye
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{activeFiles[0]?.name}</p>
                  </div>
                )}

                {hasActiveInput && (
                  <div className="space-y-3">
                    <InputTypeBadge fileExtensions={fileExtensions} apiVariant={apiVariant} contextKey={CONTEXT_IRIS_EXTRACT} />
                    <VariantStatusBanner variantStatus={variantStatus} />
                    {route.url && <RoutePreviewChip url={route.url} />}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={!hasActiveInput || isProcessing || variantStatus?.isNistBlocked || variantStatus?.isMinexBlocked}
                    variant="iris"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Eye className="mr-2 h-4 w-4" />
                        Extract Template
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>

                <VariantTestingGuide title="Variant testing guide (Extract)" rows={EXTRACT_VARIANT_GUIDE_ROWS} />
              </div>

              <div className="space-y-6">
                {requestSummary && !error && <RequestSummaryCard summary={requestSummary} />}
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {extractResult && extractResult.status === 'success' && (
                  <>
                    <Alert variant="success">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>{nistExtractSummary?.title || extractResult.details?.summaryTitle || 'Extraction Complete'}</AlertTitle>
                      <AlertDescription>
                        {extractResult.message}
                        {(nistExtractSummary?.detail || extractResult.details?.summaryDetail) && (
                          <p className="text-sm mt-2 opacity-90">
                            {nistExtractSummary?.detail || extractResult.details?.summaryDetail}
                          </p>
                        )}
                      </AlertDescription>
                    </Alert>

                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Extraction Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Iris Type:</span>
                          <Badge variant="iris">{extractResult.details.irisType}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Template size:</span>
                          <span className="font-medium">{extractResult.details.outputSizeBytes} bytes</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Format:</span>
                          <span className="font-medium text-xs text-right max-w-[60%]">
                            {extractResult.details.templateSummary.formatLabel}
                          </span>
                        </div>
                        {extractResult.details.templateSummary.parsed && (
                          <>
                            {extractResult.details.templateSummary.modelName != null && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Model:</span>
                                <span className="font-medium text-xs">{extractResult.details.templateSummary.modelName}</span>
                              </div>
                            )}
                            {extractResult.details.templateSummary.schemaVersion != null && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Schema:</span>
                                <span className="font-medium text-xs">{extractResult.details.templateSummary.schemaVersion}</span>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {showAdvanced && (
                      <Card className="timing-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timing Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Display conversion:</span>
                            <span className="font-medium">{timing.convertInput.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Image → ISO (submit):</span>
                            <span className="font-medium">{timing.convertJpeg.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Extraction:</span>
                            <span className="font-medium text-primary">
                              {timing.extractionDurationMilliSeconds.toFixed(2)} ms
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {(nistExtractResults.length ? nistExtractResults : [{ blobUrl: templateBlobUrl }]).map((result, idx) => (
                      <Button
                        key={`iris-dl-${idx}`}
                        onClick={() => {
                          if (!result.blobUrl) return;
                          const link = document.createElement('a');
                          link.href = result.blobUrl;
                          link.download = result.isBchi ? `iris-bchi-${idx}.bin` : `iris-template-${idx}.bin`;
                          link.click();
                        }}
                        variant="outline"
                        className="w-full"
                        disabled={!result.blobUrl}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {nistExtractResults.length > 1 ? `Download template #${idx}` : 'Download template'}
                      </Button>
                    ))}
                  </>
                )}

                {!extractResult && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {!extractResult && !error && !submitted && (
                  <div className="empty-state">
                    <Eye className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload an iris image and click Extract</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default IrisExtractor;
